/**
 * Layout for the primary canvas toolbar: a horizontal row — the matted bar
 * and the New Chat circle — whose home is the centre of the whole canvas
 * wrapper, `bottom-6`. It slides left only when the chat (or the FAB) would
 * overlap it, and only by the overshoot (ROUND6, D53: `rowPlacement`). The
 * bar has no vertical form. When even the full shift cannot keep the row
 * clear it gives way in two rungs (DECISIONS D7): the disc stands down
 * first while the chat is open (the open chat is the disc's own end
 * state), then the bar sheds to Cursor.
 */

import { createContext, type ReactNode, useContext, useMemo } from "react";
import {
  CANVAS_ROW_INSET_PX,
  redrawBandWidth,
  rowPlacement,
  type RowPlacement,
} from "../hooks/primaryToolbarRules";
import { useCanvasWrapperLayout } from "../hooks/useCanvasWrapperLayout";
import { useCanvasFloatingChat } from "./CanvasFloatingChatContext";

export { CANVAS_TOOLBAR_SUBMENU_GAP_PX } from "../components/toolbar/canvasToolbarLayout.constants";
export {
  CANVAS_NEW_CHAT_GAP_PX,
  CANVAS_NEW_CHAT_WIDTH_PX,
  CANVAS_PRIMARY_BAR_WIDTH_PX,
} from "../hooks/primaryToolbarRules";

/** The tray, centred on the bar, with the row's air either side. */
export const CANVAS_TRAY_WIDTH_PX = 402;
const TRAY_NEED_PX = CANVAS_TRAY_WIDTH_PX + 2 * CANVAS_ROW_INSET_PX;

export interface CanvasToolbarLayoutValue {
  /** Shared gap between toolbar shell and flyout submenus (px). */
  submenuGapPx: number;
  tooltipSide: "top";
  /** Radix dropdown / popover side for toolbar submenus. */
  menuSide: "top";
  /** Wrapper positioning for the primary toolbar row; `placeRow` sets its left. */
  containerClassName: string;
  /** The canvas band left clear of the chat, in px (0 until measured). */
  bandWidth: number;
  /** The whole canvas wrapper, in px (0 until measured). */
  wrapperWidth: number;
  /** Where a row of this rendered width stands (D53). */
  placeRow: (rowWidth: number) => RowPlacement;
  /** The bar cannot fit the band: shed to Cursor. A fit rule only — the
   * device's capability is `useIsMobileDevice`. */
  shed: boolean;
  /** The row cannot fit while a chat is open: the disc stands down. */
  discStandsDown: boolean;
  /** The band can hold the tray (true until measured). */
  trayFits: boolean;
  /** The row's slide is eased; off while a chat resize drags it 1:1. */
  rowEases: boolean;
}

const CanvasToolbarLayoutContext =
  createContext<CanvasToolbarLayoutValue | null>(null);

export interface CanvasToolbarLayoutProviderProps {
  children: ReactNode;
}

export function CanvasToolbarLayoutProvider({
  children,
}: CanvasToolbarLayoutProviderProps) {
  const { controlsMaxWidth, canvasWrapperWidth } = useCanvasWrapperLayout();
  const {
    open: chatOpen,
    bottomToolbarChatFootprintPx,
    isChatResizing,
  } = useCanvasFloatingChat();

  const value = useMemo((): CanvasToolbarLayoutValue => {
    const wrapperWidth = canvasWrapperWidth;
    // Taken off the chat's painted edge plus the named air (D55), not the
    // legacy clearance, which main's toolbar keeps reading.
    const bandWidth = redrawBandWidth({
      wrapperWidth,
      chatFootprint: bottomToolbarChatFootprintPx,
      legacyBand: controlsMaxWidth,
    });
    const clearance = wrapperWidth - bandWidth;
    const measured = bandWidth > 0;
    const placeRow = (rowWidth: number) =>
      rowPlacement({ wrapperWidth, clearance, rowWidth, chatOpen });
    const rungs = placeRow(0);
    const trayFits = !measured || bandWidth >= TRAY_NEED_PX;
    return {
      submenuGapPx: 8,
      tooltipSide: "top",
      menuSide: "top",
      // z-20: the bar's disclosures rise over the fresh-canvas landing (z-20,
      // earlier in the DOM) and stay under the chat shell (z-30), which the
      // row's placement keeps clear of. The row is the disc's 50 tall; the
      // bar centres in it. Its left slides (the row's existing travel).
      // While a chat width drag is in flight the row follows the edge 1:1
      // rather than easing after it.
      containerClassName: `absolute bottom-6 z-20 flex h-[calc(var(--size-10)+2*var(--space-1)+2*var(--border-1))] items-center${
        isChatResizing ? "" : " transition-[left] duration-200 ease-out"
      }`,
      bandWidth,
      wrapperWidth,
      placeRow,
      shed: rungs.shed,
      discStandsDown: rungs.discStandsDown,
      trayFits,
      rowEases: !isChatResizing,
    };
  }, [
    controlsMaxWidth,
    canvasWrapperWidth,
    chatOpen,
    bottomToolbarChatFootprintPx,
    isChatResizing,
  ]);

  return (
    <CanvasToolbarLayoutContext.Provider value={value}>
      {children}
    </CanvasToolbarLayoutContext.Provider>
  );
}

export function useCanvasToolbarLayout(): CanvasToolbarLayoutValue {
  const ctx = useContext(CanvasToolbarLayoutContext);
  if (!ctx) {
    throw new Error(
      "useCanvasToolbarLayout must be used within CanvasToolbarLayoutProvider",
    );
  }
  return ctx;
}

/** Optional consumer — returns null outside the provider (e.g. floating toolbars). */
export function useCanvasToolbarLayoutOptional(): CanvasToolbarLayoutValue | null {
  return useContext(CanvasToolbarLayoutContext);
}
